// Importaciones
import { forwardRef } from "react";
import { iconMap } from "./Icons";
import { brandingConfig } from "../../content/config/branding";
import { executeAction } from "../../utils/buttonActions";

/**
 * Componente de botón con variantes y animación unificada
 * - Variants: 'solid' | 'outline' | 'text'
 * - La animación visual (pseudoelemento ::before) usa SIEMPRE el color global de CSS (var(--color-hover))
 * - En todas las variantes, el texto SIEMPRE cambia a primary en hover
 * - Soporta: branding keys, hex y clases Tailwind
 */
const Button = forwardRef(
  (
    {
      children,
      background = null,
      color = "primary",
      border = false,
      icon = null,
      iconPosition = "right",
      href = null,
      disabled = false,
      size = "small",
      className = "",
      onClick,
      variant = "solid",
      ...props
    },
    ref
  ) => {
    const sizes = {
      small: {
        base: "text-sm font-semibold",
        padding: "px-6",
        minHeight: "min-h-[36px]",
        iconSize: "w-5 h-5",
      },
      medium: {
        base: "text-base font-semibold",
        padding: "px-6 py-2",
        minHeight: "min-h-[44px]",
        iconSize: "w-5 h-5",
      },
      large: {
        base: "text-lg font-semibold",
        padding: "px-44 py-4",
        minHeight: "min-h-[52px]",
        iconSize: "w-6 h-6",
      },
    };

    const currentSize = sizes[size] || sizes.medium;
    const isHex = (v) => typeof v === "string" && v.startsWith("#");
    const resolveToHex = (v) => {
      if (!v) return null;
      if (isHex(v)) return v;
      if (brandingConfig?.colors?.[v]) return brandingConfig.colors[v];
      return null;
    };

    const isOutlineLike = variant === "outline";
    const isTextLike = variant === "text";
    const outlineOrTextColor =
      isOutlineLike || isTextLike
        ? color && color !== "primary"
          ? color
          : "#F5F5F5"
        : color;

    const getStyleClasses = () => {
      let classes = [];

      // Fondo
      if (variant === "solid") {
        const bgHex = resolveToHex(background);
        if (background && !bgHex) {
          classes.push(`bg-${background}`);
        } else if (!background) {
          classes.push("bg-transparent");
        }
      } else {
        classes.push("bg-transparent");
      }

      // Text color
      if (isOutlineLike || isTextLike) {
        const c = outlineOrTextColor;
        const cHex = resolveToHex(c);
        if (c && !cHex) {
          classes.push(`text-${c}`);
        }
      } else {
        const cHex = resolveToHex(color);
        if (color && !cHex) {
          classes.push(`text-${color}`);
        } else if (background) {
          classes.push("text-white");
        } else {
          classes.push("text-primary");
        }
      }

      // Animación base
      classes.push("button-animated");
      classes.push("relative", "overflow-hidden");

      // Etiqueta de variante para CSS
      if (["solid", "outline", "text"].includes(variant)) {
        classes.push(`variant-${variant}`);
      }

      return classes.join(" ");
    };

    const getInlineStyles = () => {
      const styles = {};

      const bgHex = resolveToHex(background);
      const outlineTextHex = resolveToHex(outlineOrTextColor);
      const colorHex = resolveToHex(color);

      // Color de texto base
      const baseTextHex =
        isOutlineLike || isTextLike
          ? outlineTextHex || "#F5F5F5"
          : colorHex ||
            (background ? "#F5F5F5" : brandingConfig.colors.primary);

      if (baseTextHex) {
        styles["--text-base"] = baseTextHex;
        styles.color = baseTextHex;
      }

      styles["--text-hover"] = brandingConfig.colors.primary;

      styles["--color-hover"] = "#87d4f6";

      if (variant === "solid" && bgHex) {
        styles.backgroundColor = bgHex;
      }

      // Border para outline
      if (isOutlineLike) {
        const borderColorHex = outlineTextHex || "#F5F5F5";
        styles.borderColor = borderColorHex;
        styles["--border-base"] = borderColorHex;
        styles.borderStyle = "solid";
        styles.borderWidth = "2px";
      }

      return styles;
    };

    const renderIcon = () => {
      if (!icon) return null;

      const IconComponent = iconMap[icon];
      if (!IconComponent) {
        console.warn(
          `Icon "${icon}" not found in Icons.jsx. Available icons: ${Object.keys(
            iconMap
          ).join(", ")}`
        );
        return null;
      }

      return (
        <IconComponent className={`${currentSize.iconSize} icon-component`} />
      );
    };

    const handleClick = (event) => {
      if (onClick) {
        // Si onClick es una string, ejecutar la acción por nombre
        if (typeof onClick === 'string') {
          executeAction(onClick, { event });
        } else {
          // Si onClick es una función, ejecutarla directamente
          onClick(event);
        }
      }
    };

    const baseClasses = `
      relative inline-flex items-center justify-center
      font-sweet rounded-md cursor-pointer uppercase
      disabled:opacity-40 disabled:cursor-not-allowed
      touch-manipulation
      ${currentSize.base}
      ${currentSize.padding}
      ${currentSize.minHeight}
      ${getStyleClasses()}
      ${className}
    `
      .replace(/\s+/g, " ")
      .trim();

    const ButtonContent = () => (
      <span className="button-content relative z-10 flex items-center justify-center gap-2">
        {icon && iconPosition === "left" && (
          <span className="icon-wrapper flex-shrink-0">{renderIcon()}</span>
        )}

        <span className="button-text whitespace-nowrap">{children}</span>

        {icon && iconPosition === "right" && (
          <span className="icon-wrapper flex-shrink-0">{renderIcon()}</span>
        )}
      </span>
    );

    if (href) {
      const isExternal = href.startsWith("http") || href.startsWith("//");

      return (
        <a
          ref={ref}
          href={href}
          target={isExternal ? "_blank" : "_self"}
          rel={isExternal ? "noopener noreferrer" : ""}
          className={baseClasses}
          style={getInlineStyles()}
          onClick={handleClick}
          {...props}
        >
          <ButtonContent />
        </a>
      );
    }

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={baseClasses}
        style={getInlineStyles()}
        onClick={handleClick}
        {...props}
      >
        <ButtonContent />
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button };
export default Button;
